import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  HelpCircle,
  Maximize,
  Minimize,
  Pause,
  Play,
  RefreshCw,
  Server,
  HardDrive
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  success: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-500/20", border: "border-l-emerald-500" },
  warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-500/20", border: "border-l-amber-500" },
  error: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/20", border: "border-l-red-500" },
  running: { icon: Loader2, color: "text-blue-500", bg: "bg-blue-500/20", border: "border-l-blue-500" },
  unknown: { icon: HelpCircle, color: "text-slate-500", bg: "bg-slate-500/20", border: "border-l-slate-500" }
};

const agentTypeConfig = {
  veeam: { icon: Server, color: "text-green-400", label: "Veeam" },
  iperius: { icon: HardDrive, color: "text-orange-400", label: "Iperius" }
};

const Monitoring = () => {
  const [data, setData] = useState({ servers: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const containerRef = useRef(null);
  const scrollRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/monitoring/data`);
      setData(res.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching monitoring data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll logic
  useEffect(() => {
    if (isPaused || !scrollRef.current) return;

    const scrollContainer = scrollRef.current;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame

    const scroll = () => {
      if (isPaused) return;
      
      scrollPosition += scrollSpeed;
      
      // Reset when reaching the end
      if (scrollPosition >= scrollContainer.scrollHeight - scrollContainer.clientHeight) {
        scrollPosition = 0;
      }
      
      scrollContainer.scrollTop = scrollPosition;
    };

    const animationFrame = setInterval(scroll, 16); // ~60fps
    return () => clearInterval(animationFrame);
  }, [isPaused, data.servers.length]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Sort servers: errors first, then warnings, then running, then success, then unknown
  const sortedServers = [...data.servers].sort((a, b) => {
    const order = { error: 0, warning: 1, running: 2, success: 3, unknown: 4 };
    return (order[a.last_backup_status] || 4) - (order[b.last_backup_status] || 4);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-[#0B1120] text-white relative"
      data-testid="monitoring-page"
    >
      {/* Scanlines overlay */}
      <div className="scanlines" />

      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0B1120]/95 backdrop-blur-sm border-b border-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Shield className="text-white" size={24} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-white">ServeurOnline</h1>
              <p className="text-xs text-slate-500">Mode Monitoring</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-slate-300">{data.stats.success || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-slate-300">{data.stats.warning || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-slate-300">{data.stats.error || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-slate-300">{data.stats.running || 0}</span>
              </div>
            </div>

            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Clock size={12} />
              {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: fr })}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={fetchData}
                className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                title="Rafraichir"
                data-testid="refresh-btn"
              >
                <RefreshCw size={18} />
              </button>
              <button
                onClick={() => setIsPaused(!isPaused)}
                className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                title={isPaused ? "Reprendre" : "Pause"}
                data-testid="pause-btn"
              >
                {isPaused ? <Play size={18} /> : <Pause size={18} />}
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                title={isFullscreen ? "Quitter plein ecran" : "Plein ecran"}
                data-testid="fullscreen-btn"
              >
                {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Server grid */}
      <div
        ref={scrollRef}
        className="p-6 overflow-auto hide-scrollbar"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {sortedServers.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500">
            <div className="text-center">
              <Shield className="mx-auto h-16 w-16 mb-4 opacity-30" />
              <p>Aucun serveur a afficher</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3">
            {sortedServers.map((server) => {
              const status = statusConfig[server.last_backup_status] || statusConfig.unknown;
              const StatusIcon = status.icon;
              const agentType = agentTypeConfig[server.agent_type] || agentTypeConfig.veeam;
              const AgentIcon = agentType.icon;

              return (
                <div
                  key={server.id}
                  className={`glass-card rounded-lg border-l-4 ${status.border} p-3 transition-all duration-200 ${
                    server.last_backup_status === "error" ? "pulse-error" : ""
                  }`}
                  data-testid={`monitor-card-${server.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className={`p-1.5 rounded ${status.bg}`}>
                      <StatusIcon className={`${status.color} ${server.last_backup_status === "running" ? "animate-spin" : ""}`} size={14} />
                    </div>
                    <AgentIcon className={agentType.color} size={14} />
                  </div>
                  <h3 className="font-mono text-xs font-medium text-white truncate mb-1">
                    {server.hostname}
                  </h3>
                  <p className="text-[10px] text-slate-500 truncate">
                    {server.client_name}
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    <span className={`text-[9px] px-1 py-0.5 rounded ${server.agent_type === "iperius" ? "bg-orange-500/20 text-orange-400" : "bg-green-500/20 text-green-400"}`}>
                      {agentType.label}
                    </span>
                  </div>
                  {server.last_backup_time && (
                    <p className="text-[10px] text-slate-600 mt-1 flex items-center gap-1">
                      <Clock size={10} />
                      {formatDistanceToNow(new Date(server.last_backup_time), { locale: fr })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer ticker for errors */}
      {data.stats.error > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-red-500/10 border-t border-red-500/30 py-2 px-4">
          <div className="flex items-center gap-4 overflow-hidden">
            <XCircle className="text-red-500 flex-shrink-0" size={16} />
            <div className="whitespace-nowrap animate-[ticker_30s_linear_infinite]">
              {sortedServers
                .filter((s) => s.last_backup_status === "error")
                .map((s) => (
                  <span key={s.id} className="mx-4 text-sm text-red-400">
                    {s.client_name} / {s.hostname}: {s.last_backup_message || "Erreur de sauvegarde"}
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Monitoring;
